/**
 * Round profile picture with an initials fallback, used in the header, Settings, Team rosters
 * and the Organization page.
 *
 * @module apps/web/src/components/ui/user-avatar
 */
import { useEffect, useState } from 'react';
import { initialsFor, loadAvatarObjectUrl } from '@/lib/avatarImage';
import { cn } from '@/lib/utils';

/** Inputs for {@link UserAvatar}. */
export interface UserAvatarProps {
  /** Person's display name, used for the initials and the accessible label. */
  name?: string | null;
  /** Person's email, used for the initial when there is no name. */
  email?: string | null;
  /** Picture path from the API (`avatar_url`); null or omitted shows initials. */
  avatarUrl?: string | null;
  /** Extra classes; size defaults to 2rem (`h-8 w-8`) and can be overridden here. */
  className?: string;
}

/**
 * Show a person's picture, loaded once per URL through the authenticated image route, or their
 * initials while it loads, when there is none, or when it cannot be fetched.
 */
export function UserAvatar({ name, email, avatarUrl, className }: UserAvatarProps) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    setSrc(null);
    if (avatarUrl) loadAvatarObjectUrl(avatarUrl).then(url => active && setSrc(url));
    return () => {
      active = false;
    };
  }, [avatarUrl]);
  const label = name || email || 'User';
  return (
    <span
      className={cn(
        'bg-muted text-muted-foreground inline-flex h-8 w-8 flex-shrink-0 select-none items-center justify-center overflow-hidden rounded-full text-xs font-semibold',
        className
      )}
      role="img"
      aria-label={label}
      title={label}
    >
      {src ? (
        <img src={src} alt="" className="h-full w-full object-cover" />
      ) : (
        initialsFor(name, email)
      )}
    </span>
  );
}
